import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Chip,
	Stack,
	TextField,
	MenuItem,
	Button,
	CircularProgress,
	TablePagination,
	Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useGetList } from 'react-admin';
import { Link } from 'react-router-dom';

import { getActivity, type ActivityItem, type ActivityMetrics } from '../../Services/activity';
import {
	activityImpersonalVerbs,
	activityVerbs,
	renderActivityParts,
	typeLabel,
} from './activityLabels';

// Vue ADMIN du flux d'activité : tout le parc, mêmes filtres que l'espace partenaire
// (période / client / type) et mêmes phrases.
//
// Mise en page en FIL GROUPÉ PAR JOUR plutôt qu'en tableau : une phrase complète ne
// tient pas dans une colonne, et un tableau à 6 colonnes déborde dès que le nom du
// client ou du projet est long. Ici la phrase occupe toute la largeur, et le contexte
// (client · projet · heure) passe en sous-ligne discrète.

const PERIODS = [
	{ days: 7, label: '7 derniers jours' },
	{ days: 30, label: '30 derniers jours' },
	{ days: 90, label: '90 derniers jours' },
];

const PAGE_SIZES = [25, 50, 100, 200];

// Liens du contexte : on garde la discrétion de la sous-ligne (même taille, même
// couleur héritée) mais soulignés au survol pour signaler qu'ils sont cliquables.
const linkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };

// Le nom de l'acteur est DANS la phrase : le souligner en permanence hacherait la
// lecture de tout le fil. On garde donc la couleur du texte et on ne souligne rien —
// gras suffit à signaler qu'il est cliquable.
const actorLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'none', fontWeight: 600 };

// #region formatters
const dayKey = (iso: string) => {
	const d = new Date(iso);
	return isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
};

const dayLabel = (key: string) => {
	const d = new Date(`${key}T12:00:00Z`);
	if (isNaN(d.getTime())) return key;
	const today = new Date().toISOString().slice(0, 10);
	const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
	if (key === today) return "Aujourd'hui";
	if (key === yesterday) return 'Hier';
	return d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });
};

const hourLabel = (iso: string) => {
	const d = new Date(iso);
	return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};
// #endregion

// #region MetricsRow
// Mêmes métriques que côté partenaire, produites par le même read model. L7/L28 =
// jours DISTINCTS actifs, calculés sur l'activité HUMAINE uniquement (les events
// machine sont exclus côté back, sinon les crons rendraient tout le monde « actif »).
const MetricsRow = ({ metrics, total }: { metrics: ActivityMetrics; total: number }) => (
	<Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
		<Chip label={`${total} événements`} color="primary" />
		<Chip label={`Jours actifs 7 j : ${metrics.l7}/7`} />
		<Chip label={`Jours actifs 28 j : ${metrics.l28}/28`} />
		<Chip
			label={`Tendance 7 j : ${metrics.trendL7 > 0 ? '+' : ''}${metrics.trendL7}`}
			color={metrics.trendL7 < 0 ? 'warning' : 'default'}
		/>
		<Chip label={`Types distincts : ${metrics.breadth}`} />
	</Box>
);
// #endregion

// #region ActivityRow
// Une ligne = une phrase pleine largeur + le contexte en dessous. La puce grise
// distingue les events machine (facturation, synchro) de l'activité humaine.
const ActivityRow = ({ item }: { item: ActivityItem }) => {
	// Liens vers les fiches admin. ⚠️ La fiche projet s'adresse par `projectId` (clé
	// primaire), PAS par le folderId — d'où son ajout au read model. La fiche BA, elle,
	// s'adresse bien par l'UUID qu'on a déjà.
	// Pas de lien si l'id manque : on affiche alors le libellé en texte simple plutôt
	// qu'un lien mort.
	const baLabel = item.scope.billingAccountName || item.scope.billingAccountId;
	const projectLabel = item.scope.projectName || item.scope.projectFolderId;

	// L'acteur est rendu à part pour pouvoir le rendre cliquable vers sa fiche. Pas de
	// lien si l'event est machine ou si l'acteur n'a pas d'id numérique (events dont
	// l'émetteur ne publie que l'UID Firebase) : on affiche alors le nom en texte.
	const { actor, text } = renderActivityParts(item);
	const actorUserId = item.actor.userId;

	return (
		<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 1 }}>
			<Box
				sx={{
					mt: '7px',
					width: 8,
					height: 8,
					borderRadius: '50%',
					flexShrink: 0,
					bgcolor: item.isMachine ? 'grey.400' : 'info.main',
				}}
			/>
			<Box sx={{ minWidth: 0, flexGrow: 1 }}>
				<Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
					{actor &&
						(actorUserId ? (
							<Link to={`/users/${actorUserId}`} style={actorLinkStyle}>
								{actor}
							</Link>
						) : (
							actor
						))}
					{actor ? ' ' : ''}
					{text}
				</Typography>
				<Typography variant="caption" color="text.secondary" component="div">
					{hourLabel(item.occurredAt)}
					{baLabel && (
						<>
							{' · '}
							{item.scope.billingAccountId ? (
								<Link to={`/billing-accounts/${item.scope.billingAccountId}`} style={linkStyle}>
									{baLabel}
								</Link>
							) : (
								baLabel
							)}
						</>
					)}
					{projectLabel && (
						<>
							{' · '}
							{item.scope.projectId ? (
								<Link to={`/projects/${item.scope.projectId}`} style={linkStyle}>
									{projectLabel}
								</Link>
							) : (
								projectLabel
							)}
						</>
					)}
				</Typography>
			</Box>
			{/* Type brut gardé à droite, discret : indispensable pour diagnostiquer en
			    interne, mais il ne doit plus voler la vedette à la phrase. */}
			<Chip
				size="small"
				variant="outlined"
				label={item.type}
				sx={{ flexShrink: 0, fontSize: '0.68rem', color: 'text.secondary' }}
			/>
		</Box>
	);
};
// #endregion

// #region DayGroup
const DayGroup = ({ day, items }: { day: string; items: ActivityItem[] }) => (
	<Box sx={{ mb: 2 }}>
		<Typography
			variant="overline"
			color="text.secondary"
			sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}
		>
			{dayLabel(day)} · {items.length} événement{items.length > 1 ? 's' : ''}
		</Typography>
		<Divider sx={{ mb: 1 }} />
		{items.map((item, i) => (
			<ActivityRow key={`${item.type}-${item.occurredAt}-${i}`} item={item} />
		))}
	</Box>
);
// #endregion

// #region ActivitiesList
export const ActivitiesList = () => {
	const [days, setDays] = useState(30);
	const [billingAccountId, setBillingAccountId] = useState('');
	const [type, setType] = useState('');
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(50);

	const [items, setItems] = useState<ActivityItem[]>([]);
	const [total, setTotal] = useState(0);
	const [metrics, setMetrics] = useState<ActivityMetrics>({
		lastSeenAt: null,
		l7: 0,
		l28: 0,
		trendL7: 0,
		breadth: 0,
	});
	const [loading, setLoading] = useState(true);
	const [seenTypes, setSeenTypes] = useState<string[]>([]);

	// Liste des clients pour le filtre : ressource react-admin déjà câblée.
	const { data: billingAccounts } = useGetList('billing-accounts', {
		pagination: { page: 1, perPage: 500 },
		sort: { field: 'name', order: 'ASC' },
	});

	const load = useCallback(async () => {
		setLoading(true);
		const res = await getActivity({
			days,
			billingAccountId: billingAccountId || undefined,
			type: type || undefined,
			limit: pageSize,
			offset: page * pageSize,
		});
		setItems(res.feed.items);
		setTotal(res.feed.total);
		setMetrics(res.metrics);
		setLoading(false);
	}, [days, billingAccountId, type, page, pageSize]);

	useEffect(() => {
		load();
	}, [load]);

	// Les options de type ne doivent pas se réduire quand un filtre est actif, sinon on
	// ne peut plus en sortir (même piège que côté partenaire). On part du catalogue
	// connu, complété par les types réellement vus.
	useEffect(() => {
		setSeenTypes((prev) => {
			const merged = new Set(prev);
			items.forEach((i) => merged.add(i.type));
			return merged.size === prev.length ? prev : Array.from(merged);
		});
	}, [items]);

	const typeOptions = useMemo(
		() =>
			Array.from(
				new Set([
					...Object.keys(activityVerbs),
					...Object.keys(activityImpersonalVerbs),
					...seenTypes,
				])
			).sort(),
		[seenTypes]
	);

	// Groupement par jour : les items arrivent déjà triés du plus récent au plus ancien.
	const groups = useMemo(() => {
		const map = new Map<string, ActivityItem[]>();
		items.forEach((item) => {
			const key = dayKey(item.occurredAt);
			const list = map.get(key);
			if (list) list.push(item);
			else map.set(key, [item]);
		});
		return Array.from(map.entries());
	}, [items]);

	return (
		<Card sx={{ mt: 2 }}>
			<CardContent>
				<Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
					<Typography variant="h5">Activités</Typography>
					<Typography variant="body2" color="text.secondary">
						Tout le parc — mêmes filtres que l'espace partenaire
					</Typography>
					<Button startIcon={<RefreshIcon />} onClick={load} size="small">
						Rafraîchir
					</Button>
					{metrics.lastSeenAt && (
						<Typography variant="caption" color="text.secondary">
							Dernière activité humaine : {new Date(metrics.lastSeenAt).toLocaleString('fr-FR')}
						</Typography>
					)}
				</Box>

				<MetricsRow metrics={metrics} total={total} />

				{/* Box flex et non Stack : le `spacing` de Stack passe par des marges sur les
				    enfants, ce qui casse la mise en page dès qu'on ajoute flexWrap (il
				    faudrait useFlexGap). Un Box avec gap se comporte de façon prévisible. */}
				<Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
					<TextField
						select
						size="small"
						label="Période"
						value={days}
						onChange={(e) => {
							setPage(0);
							setDays(Number(e.target.value));
						}}
						// fullWidth={false} explicite : le thème react-admin impose fullWidth
						// par défaut aux champs de formulaire, ce qui les met chacun sur sa
						// ligne. flex: '0 0 auto' neutralise en plus toute flex-basis héritée.
						fullWidth={false}
						sx={{ width: 180, flex: '0 0 auto' }}
					>
						{PERIODS.map((p) => (
							<MenuItem key={p.days} value={p.days}>
								{p.label}
							</MenuItem>
						))}
					</TextField>

					<TextField
						select
						size="small"
						label="Client"
						value={billingAccountId}
						onChange={(e) => {
							setPage(0);
							setBillingAccountId(e.target.value);
						}}
						fullWidth={false}
						sx={{ width: 240, flex: '0 0 auto' }}
					>
						<MenuItem value="">Tous les clients</MenuItem>
						{(billingAccounts ?? []).map((ba: any) => (
							<MenuItem key={ba.id} value={ba.id}>
								{ba.name || ba.id}
							</MenuItem>
						))}
					</TextField>

					<TextField
						select
						size="small"
						label="Type d'événement"
						value={type}
						onChange={(e) => {
							setPage(0);
							setType(e.target.value);
						}}
						fullWidth={false}
						sx={{ width: 280, flex: '0 0 auto' }}
					>
						<MenuItem value="">Tous les types</MenuItem>
						{typeOptions.map((t) => (
							<MenuItem key={t} value={t}>
								{typeLabel(t)}
							</MenuItem>
						))}
					</TextField>
				</Box>

				{loading && (
					<Stack alignItems="center" sx={{ py: 4 }}>
						<CircularProgress size={24} />
					</Stack>
				)}

				{!loading && items.length === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
						Aucune activité pour ces filtres. Élargis la période ou retire le filtre de type.
					</Typography>
				)}

				{!loading && items.length > 0 && (
					<>
						{groups.map(([day, dayItems]) => (
							<DayGroup key={day} day={day} items={dayItems} />
						))}
						<TablePagination
							component="div"
							count={total}
							page={page}
							rowsPerPage={pageSize}
							rowsPerPageOptions={PAGE_SIZES}
							onPageChange={(_, p) => setPage(p)}
							onRowsPerPageChange={(e) => {
								setPageSize(Number(e.target.value));
								setPage(0);
							}}
							labelRowsPerPage="Par page"
						/>
					</>
				)}
			</CardContent>
		</Card>
	);
};
// #endregion
